import type {
  AccordionDesignTokens,
  AlloyLegacyThemeDesignTokens,
} from '../types';
import type { Weave3AccordionInput } from '../types/weave3Types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

/** Colours may contain spaces (`rgba(0, 0, 0, .1)`), so the tail is rejoined. */
const splitBoxShadow = (boxShadow: string) => {
  const parts = boxShadow.split(' ');
  const [x, y, blur, spread] = parts.slice(0, 4);

  return {
    'box-shadow-x': { focus: x },
    'box-shadow-y': { focus: y },
    'box-shadow-blur': { focus: blur },
    'box-shadow-spread': { focus: spread },
    'box-shadow-color': { focus: parts.slice(4).join(' ') },
  };
};

export const getAccordionTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  spacing: Record<string, string>,
): AccordionDesignTokens => ({
  'background-color': theme.tokens.colors.background.secondary.value,
  'box-shadow-inset': { focus: undefined },
  ...splitBoxShadow(theme.halos.focus),
  'font-family': theme.typography.labelSmall.fontFamily,
  'font-weight': theme.typography.labelSmall.fontWeight,
  'font-size': theme.typography.labelSmall.fontSize,
  'line-height': theme.typography.labelSmall.lineHeight,
  height: '32px',
  icon: {
    width: '16px',
    size: 16,
    'margin-left': spacing.s,
    'transition-property': 'transform',
    'transition-duration': '0.15s',
    'transition-timing-function': 'linear',
    'transform-expanded': 'rotate(0deg)',
    'transform-collapsed': 'rotate(-90deg)',
    fill: undefined,
  },
  'padding-bottom': undefined,
  'padding-left': undefined,
  'padding-right': undefined,
  'padding-top': undefined,
  'margin-top': spacing[2],
  'text-color': theme.tokens.colors.text.primary.value,
  secondary: {
    'text-color': theme.tokens.colors.text.secondary.value,
  },
  label: {
    'margin-top': spacing.xs,
    'margin-bottom': spacing.xs,
    'margin-left': spacing.xs,
  },
});

/** Weave's chevron points right when collapsed, so the rotation pair differs from Alloy's. */
export const getAccordionTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): AccordionDesignTokens => {
  const accordion = tokens?.component?.accordion;
  const label = accordion?.['indicator-left']?.label;

  return {
    ...accordion,
    'font-family': fontFamily,
    'margin-top': '0',
    icon: {
      ...accordion?.icon,
      size: 10,
      'margin-left': '0',
      'transform-expanded': 'rotate(90deg)',
      'transform-collapsed': 'rotate(0deg)',
    },
    secondary: {
      'text-color': tokens?.semantic?.['text-color']?.light,
    },
    label: {
      'margin-top': label?.['padding-top'],
      'margin-bottom': label?.['padding-bottom'],
      'margin-left': label?.['padding-left'],
    },
  };
};

/**
 * `height` and `icon.height` are read swapped: the snapshot reports `16px` /
 * `36px`, the inverse of Weave 2, and reading it literally collapses the header
 * row. Snapshot defect logged against DSGN-2473 (tokens are a frozen import).
 */
export const getAccordionTokensWeave3 = (
  accordion?: Weave3AccordionInput,
  fontFamily = 'Artifakt Element, sans-serif',
): AccordionDesignTokens => {
  const label = accordion?.['indicator-left']?.label;

  return {
    'background-color': accordion?.['background-color']?.default,
    'box-shadow-inset': { focus: accordion?.['box-shadow-inset']?.focus },
    'box-shadow-x': { focus: accordion?.['box-shadow-x']?.focus },
    'box-shadow-y': { focus: accordion?.['box-shadow-y']?.focus },
    'box-shadow-blur': { focus: accordion?.['box-shadow-blur']?.focus },
    'box-shadow-spread': { focus: accordion?.['box-shadow-spread']?.focus },
    'box-shadow-color': { focus: accordion?.['box-shadow-color']?.focus },
    'font-family': fontFamily,
    'font-weight': accordion?.['font-weight'],
    'font-size': accordion?.['font-size'],
    'line-height': accordion?.['line-height'],
    height: accordion?.icon?.height,
    icon: {
      width: accordion?.height,
      size: 16,
      'margin-left': '0',
      'transition-property': 'transform',
      'transition-duration': '0.15s',
      'transition-timing-function': 'linear',
      'transform-expanded': 'rotate(90deg)',
      'transform-collapsed': 'rotate(0deg)',
      fill: {
        default: accordion?.icon?.fill?.default,
        hover: accordion?.icon?.fill?.hover,
      },
    },
    'padding-bottom': accordion?.['padding-bottom'],
    'padding-left': accordion?.['padding-left'],
    'padding-right': accordion?.['padding-right'],
    'padding-top': accordion?.['padding-top'],
    'margin-top': '0',
    'text-color': accordion?.['text-color']?.default,
    secondary: {
      'text-color': accordion?.content?.['text-color'],
    },
    label: {
      'margin-top': label?.['padding-top'],
      'margin-bottom': label?.['padding-bottom'],
      'margin-left': label?.['padding-left'],
    },
  };
};
